import { useEffect, useState } from "react";
import { X, Loader2, Search, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useBodyScrollLock } from "../../lib/useBodyScrollLock";

/**
 * V156 — Create Assignment dialog (Phase 1: manual "Log Accepted Lead").
 *
 * Two-step:
 *   1. Search + optionally link an existing Anand demo-registration/inquiry
 *   2. Fill BMT lead-id, tutor id/name, and confirm student basics.
 *
 * On 409 duplicate, we surface an admin-only "Force create" toggle.
 */

const EMPTY = {
    bookmytutor_lead_id: "",
    tutor_id: "",
    tutor_name: "",
    tutor_qualification: "",
    student_name: "",
    grade: "",
    board: "",
    subject: "",
    city: "",
    class_mode: "",
    confirm_overwrite: false,
};

export default function CreateAssignmentDialog({ onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY);
    const [linked, setLinked] = useState(null);   // { id, kind, name, ... }
    const [inquiryQuery, setInquiryQuery] = useState("");
    const [inquiryResults, setInquiryResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useBodyScrollLock(true);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Debounced search
    useEffect(() => {
        if (!inquiryQuery || inquiryQuery.trim().length < 2) {
            setInquiryResults([]);
            return undefined;
        }
        const t = setTimeout(() => {
            setSearching(true);
            api.get(`/admin/assignments/inquiry-search?q=${encodeURIComponent(inquiryQuery.trim())}`)
                .then((r) => setInquiryResults(r.data?.items || []))
                .catch(() => setInquiryResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(t);
    }, [inquiryQuery]);

    const linkInquiry = (r) => {
        setLinked(r);
        set("student_name", r.name || form.student_name);
        set("grade", r.grade || form.grade);
        set("board", r.board || form.board);
        set("subject", r.subject || form.subject);
        set("city", r.city || form.city);
        setInquiryResults([]);
        setInquiryQuery("");
    };

    const unlink = () => setLinked(null);

    const submit = async () => {
        setError(null);
        if (!form.bookmytutor_lead_id.trim()) return setError("BookMyTutor Lead ID is required.");
        if (!form.tutor_id.trim()) return setError("Tutor ID is required.");
        if (!form.tutor_name.trim()) return setError("Tutor name is required.");
        if (!form.student_name.trim()) return setError("Student name is required.");
        setSaving(true);
        try {
            const body = {
                ...form,
                inquiry_id: linked?.id,
                inquiry_kind: linked?.kind,
            };
            const { data } = await api.post("/admin/assignments/from-inquiry", body);
            onCreated?.(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "duplicate_bmt_lead") {
                setError(
                    `Already exists as ${detail.assignment_code}. Tick "Force create despite duplicate" to override.`,
                );
            } else {
                setError(typeof detail === "string" ? detail : "Failed to create assignment.");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            style={{ width: "100vw", height: "100vh" }}
            role="dialog"
            aria-modal="true"
            data-testid="assignment-create-dialog"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div
                className="bg-white w-full sm:w-auto sm:max-w-2xl sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Log Accepted Lead</div>
                        <div className="text-lg font-heading font-bold text-[#0F172A]">New Assignment from BookMyTutor</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-600" data-cm-compact aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-4">
                    {/* Step 1 — optional inquiry link */}
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Step 1 · Link an existing Anand inquiry (optional)</div>
                        {linked ? (
                            <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-emerald-800 truncate">{linked.name}</div>
                                    <div className="text-[11px] text-emerald-700">{linked.kind} · {linked.grade || "—"} · {linked.subject || "—"} · {linked.city || "—"}</div>
                                </div>
                                <button onClick={unlink} className="text-xs text-emerald-800 underline">Unlink</button>
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <Search className="absolute top-2.5 left-3 w-4 h-4 text-slate-400" />
                                    <input
                                        value={inquiryQuery}
                                        onChange={(e) => setInquiryQuery(e.target.value)}
                                        placeholder="Search demo-registrations by student name, phone, email, city…"
                                        data-testid="inquiry-search-input"
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                                    />
                                    {searching && <Loader2 className="absolute top-2.5 right-3 w-4 h-4 animate-spin text-slate-400" />}
                                </div>
                                {inquiryResults.length > 0 && (
                                    <ul className="mt-1 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white shadow-sm">
                                        {inquiryResults.map((r) => (
                                            <li key={`${r.kind}-${r.id}`}>
                                                <button onClick={() => linkInquiry(r)} className="w-full text-left px-3 py-2 hover:bg-cyan-50">
                                                    <div className="text-sm font-semibold text-slate-800">{r.name}</div>
                                                    <div className="text-[11px] text-slate-500">{r.kind} · {r.grade || "—"} · {r.subject || "—"} · {r.city || "—"}</div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <p className="text-[11px] text-slate-500 mt-1">Optional — leave blank if the BookMyTutor lead has no matching Anand demo record.</p>
                            </>
                        )}
                    </div>

                    {/* Step 2 — form */}
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Step 2 · BMT + Tutor details</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="BookMyTutor Lead ID *" value={form.bookmytutor_lead_id} onChange={(v) => set("bookmytutor_lead_id", v)} testid="bmt-lead-id" placeholder="BMT-1048" />
                            <Field label="Tutor ID *" value={form.tutor_id} onChange={(v) => set("tutor_id", v)} testid="tutor-id" placeholder="T-77" />
                            <Field label="Tutor Name *" value={form.tutor_name} onChange={(v) => set("tutor_name", v)} testid="tutor-name" />
                            <Field label="Tutor Qualification" value={form.tutor_qualification} onChange={(v) => set("tutor_qualification", v)} placeholder="M.Sc Math" />
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Student basics</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Student Name *" value={form.student_name} onChange={(v) => set("student_name", v)} testid="student-name" />
                            <Field label="Grade" value={form.grade} onChange={(v) => set("grade", v)} placeholder="Class 10" />
                            <Field label="Board" value={form.board} onChange={(v) => set("board", v)} placeholder="CBSE" />
                            <Field label="Subject" value={form.subject} onChange={(v) => set("subject", v)} placeholder="Mathematics" />
                            <Field label="City" value={form.city} onChange={(v) => set("city", v)} placeholder="Mumbai" />
                            <Field label="Class Mode" value={form.class_mode} onChange={(v) => set("class_mode", v)} placeholder="Online" />
                        </div>
                    </div>

                    {error && (
                        <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2" data-testid="create-error">
                            {error}
                        </div>
                    )}

                    <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                            type="checkbox"
                            checked={!!form.confirm_overwrite}
                            onChange={(e) => set("confirm_overwrite", e.target.checked)}
                            data-testid="confirm-overwrite"
                        />
                        Force create despite duplicate BookMyTutor lead ID (admin override)
                    </label>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                        <button
                            onClick={submit}
                            disabled={saving}
                            data-testid="assignment-create-submit"
                            data-cm-primary
                            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Create Assignment
                        </button>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                        <Sparkles className="w-3 h-3" />
                        Parent contact will be pulled from the linked inquiry if any, stored internally, and NEVER shown to the tutor.
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">{label}</div>
            <input
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
            />
        </div>
    );
}
